import React, { useEffect, useRef, useState } from 'react'
import { Check, Search, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import Filters, { FilterGroup } from '../organisms/Filters'
import type { OrderType, DealType, SortBy, SortDirection } from '@/types/otc'

export interface OTCFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  type: OrderType | ''
  onTypeChange: (value: OrderType | '') => void
  assetType: DealType | ''
  onAssetTypeChange: (value: DealType | '') => void
  trancheSize: string
  onTrancheSizeChange: (value: string) => void
  /**
   * Hide the controls that read a financial column — the order-size buckets
   * and the "Offered Amount" sort. See the comment above `trancheSizeOptions`.
   */
  financialsHidden?: boolean
  sortBy: SortBy | ''
  onSortByChange: (value: SortBy | '') => void
  sortDirection: SortDirection | ''
  onSortDirectionChange: (value: SortDirection | '') => void
  className?: string
  layout?: 'horizontal' | 'grid'
}

interface FilterOption {
  value: string
  label: string
}

interface DesktopSelectProps {
  value: string
  onChange: (value: string) => void
  options: FilterOption[]
}

const DesktopSelect: React.FC<DesktopSelectProps> = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const selectedOption = options.find((option) => option.value === value) ?? options[0]

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current) return
      if (!rootRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1">
      <button
        type="button"
        className="flex w-full min-w-0 items-center gap-2 bg-[rgba(255,255,255,0.44)] dark:bg-[#1c1c1c] pl-4 pr-3 py-[17px]"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="w-full truncate text-left font-plus-jakarta text-[13px] font-normal leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
          {selectedOption.label}
        </span>
        <ChevronDown className={cn('h-5 w-5 shrink-0 text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-[calc(100%+6px)] z-40 w-full overflow-hidden rounded-md border border-[rgba(45,41,35,0.16)] dark:border-[#0a0a0a] bg-white dark:bg-[#1c1c1c] shadow-[0_8px_24px_rgba(45,41,35,0.12)] dark:shadow-[0_8px_24px_rgba(0,0,0,0.55)]">
          {options.map((option) => {
            const isSelected = option.value === value

            return (
              <button
                key={option.value}
                type="button"
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left transition-colors',
                  isSelected
                    ? 'bg-[rgba(45,41,35,0.08)] dark:bg-[#0f0f0f]'
                    : 'hover:bg-[rgba(45,41,35,0.04)] dark:hover:bg-[#0f0f0f]'
                )}
                onClick={() => {
                  onChange(option.value)
                  setIsOpen(false)
                }}
              >
                <span className="truncate font-plus-jakarta text-[13px] font-normal leading-[18px] text-[#2d2923] dark:text-[#fdfdfd]">
                  {option.label}
                </span>
                {isSelected && <Check className="h-4 w-4 shrink-0 text-[#2d2923] dark:text-[#fdfdfd]" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

const toOrderType = (value: string): OrderType | '' => {
  if (value === 'BUY' || value === 'SELL') return value
  return ''
}

const toDealType = (value: string): DealType | '' => {
  if (value === 'LIQUID_TOKEN' || value === 'SAFT' || value === 'EQUITY' || value === 'EQUITY_WARRANT' || value === 'NFT') {
    return value
  }
  return ''
}

const toSortBy = (value: string): SortBy | '' => {
  if (value === 'DATE' || value === 'PROJECT_NAME' || value === 'OFFERED_AMOUNT') return value
  return ''
}

const toSortDirection = (value: string): SortDirection | '' => {
  if (value === 'ASC' || value === 'DESC') return value
  return ''
}

const OTCFilters: React.FC<OTCFiltersProps> = ({
  search,
  onSearchChange,
  type,
  onTypeChange,
  assetType,
  onAssetTypeChange,
  trancheSize,
  onTrancheSizeChange,
  sortBy,
  onSortByChange,
  sortDirection,
  onSortDirectionChange,
  className,
  layout = 'horizontal',
  financialsHidden = false
}) => {
  const typeOptions: FilterOption[] = [
    { value: '', label: 'All Types' },
    { value: 'BUY', label: 'Buy' },
    { value: 'SELL', label: 'Sell' }
  ]

  const assetTypeOptions: FilterOption[] = [
    { value: '', label: 'All Assets' },
    { value: 'LIQUID_TOKEN', label: 'Liquid Token' },
    { value: 'SAFT', label: 'SAFT' },
    { value: 'EQUITY', label: 'Equity' },
    { value: 'EQUITY_WARRANT', label: 'Equity Warrant' },
    { value: 'NFT', label: 'NFT' }
  ]

  // 🔴 Order-size buckets are a FINANCIAL filter: they map to
  // `min_offered_amount` / `max_offered_amount`, which the public listing
  // refuses (400) precisely because a filter over a hidden column plus the
  // envelope's `total` is an oracle — two requests bracket a value, ten pin it
  // down. Sorting by Offered Amount is the same read by another route. Neither
  // control is offered when the amounts are not.
  const trancheSizeOptions: FilterOption[] = [
    { value: '', label: 'All Sizes' },
    { value: '10k-100k', label: '10k - 100k' },
    { value: '100k-250k', label: '100k - 250k' },
    { value: '250k-500k', label: '250k - 500k' },
    { value: '1m+', label: '1m+' }
  ]

  const sortByOptions: FilterOption[] = [
    { value: '', label: 'Sort By' },
    { value: 'DATE', label: 'Date' },
    { value: 'PROJECT_NAME', label: 'Project Name' },
    ...(financialsHidden ? [] : [{ value: 'OFFERED_AMOUNT', label: 'Offered Amount' }])
  ]

  const sortDirectionOptions: FilterOption[] = [
    { value: '', label: 'Sort Order' },
    { value: 'ASC', label: 'Ascending' },
    { value: 'DESC', label: 'Descending' }
  ]

  if (layout === 'horizontal') {
    return (
      <div className={cn('flex w-full items-center gap-2', className)}>
        <div className="flex w-[677px] shrink-0 items-center gap-2 bg-[rgba(255,255,255,0.44)] dark:bg-[#1c1c1c] px-3 py-[18px]">
          <Search className="h-4 w-4 shrink-0 text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by project name"
            className="w-full bg-transparent border-none p-0 font-plus-jakarta text-[13px] font-normal leading-[18px] text-[#2d2923] dark:text-[#fdfdfd] placeholder:text-[rgba(45,41,35,0.48)] dark:placeholder:text-[rgba(253,253,253,0.48)] outline-none"
          />
        </div>

        <DesktopSelect value={type} onChange={(next) => onTypeChange(toOrderType(next))} options={typeOptions} />
        <DesktopSelect value={assetType} onChange={(next) => onAssetTypeChange(toDealType(next))} options={assetTypeOptions} />
        {!financialsHidden && (
          <DesktopSelect value={trancheSize} onChange={onTrancheSizeChange} options={trancheSizeOptions} />
        )}
        <DesktopSelect value={sortBy} onChange={(next) => onSortByChange(toSortBy(next))} options={sortByOptions} />
        <DesktopSelect value={sortDirection} onChange={(next) => onSortDirectionChange(toSortDirection(next))} options={sortDirectionOptions} />
      </div>
    )
  }

  const filterGroups: FilterGroup[] = [
    {
      id: 'type',
      label: 'Type',
      value: type,
      onChange: onTypeChange as (value: string) => void,
      options: typeOptions,
    },
    {
      id: 'assetType',
      label: 'Asset Type',
      value: assetType,
      onChange: onAssetTypeChange as (value: string) => void,
      options: assetTypeOptions,
    },
    ...(financialsHidden
      ? []
      : [
          {
            id: 'trancheSize',
            label: 'Order Size',
            value: trancheSize,
            onChange: onTrancheSizeChange,
            options: trancheSizeOptions,
          },
        ]),
    {
      id: 'sortBy',
      label: 'Sort By',
      value: sortBy,
      onChange: onSortByChange as (value: string) => void,
      options: sortByOptions,
    },
    {
      id: 'sortDirection',
      label: 'Sort Order',
      value: sortDirection,
      onChange: onSortDirectionChange as (value: string) => void,
      options: sortDirectionOptions,
    }
  ]

  return (
    <Filters
      searchPlaceholder="Search by project name"
      searchValue={search}
      onSearchChange={onSearchChange}
      filterGroups={filterGroups}
      className={className}
      layout={layout}
    />
  )
}

export default OTCFilters
